import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  ShoppingCart,
  Settings2,
  ListChecks,
  LogOut,
  Home,
  ChevronRight,
  Newspaper,
} from "lucide-react";
import { useAdminAuth } from "../../lib/adminAuth";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { fetchBlogStats } from "../../lib/adminApi";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/commandes", label: "Mes commandes", icon: ShoppingCart, testid: "admin-nav-orders" },
  { to: "/admin/fournisseurs", label: "Fournisseurs", icon: Settings2, testid: "admin-nav-suppliers" },
  { to: "/admin/blog", label: "Blog", icon: Newspaper, testid: "admin-nav-blog", badgeKey: "pending" },
  { to: "/admin/clics", label: "Clics bruts", icon: ListChecks, testid: "admin-nav-clicks" },
];

export default function AdminLayout() {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadStats = () => {
      fetchBlogStats()
        .then((d) => setStats(d.counts || null))
        .catch(() => {});
    };
    loadStats();
    const t = setInterval(loadStats, 30000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success("Déconnexion effectuée");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="admin-layout" className="mx-auto max-w-7xl px-4 py-6 sm:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-cyan-300">
            <Home className="h-4 w-4" />
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="font-mono uppercase tracking-widest text-cyan-400">
            Back-office Autopièce Malin
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-slate-500 sm:inline">
            Connecté : <span className="font-mono text-slate-300">{user?.email}</span>
          </span>
          <Button
            data-testid="admin-logout-button"
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="gap-2 border-slate-700 bg-slate-900/70 text-slate-200 hover:border-red-500/50 hover:text-red-300"
          >
            <LogOut className="h-4 w-4" />
            Déconnexion
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <aside className="surface-card h-fit rounded-2xl p-2">
          <nav className="flex flex-row gap-1 overflow-x-auto lg:flex-col lg:gap-1">
            {NAV.map(({ to, label, icon: Icon, end, testid, badgeKey }) => {
              const badgeCount = badgeKey && stats ? stats[badgeKey] : 0;
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium transition whitespace-nowrap ${
                      isActive
                        ? "bg-cyan-500/15 text-cyan-200 ring-1 ring-cyan-500/30"
                        : "text-slate-300 hover:bg-slate-800 hover:text-cyan-300"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1">{label}</span>
                  {badgeCount > 0 && (
                    <span
                      data-testid={`admin-nav-badge-${testid}`}
                      className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-slate-950"
                    >
                      {badgeCount}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        <main data-testid="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
